import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { positionsApi } from '@/AdminPanel/api/positions.api';
import { rolesActionsApi } from '@/AdminPanel/api/roles-actions.api';
import { usersApi } from '@/AdminPanel/api/users.api';
import type { TPosition } from '@/AdminPanel/pages/Users/types/position.types';
import type { TRoleAction } from '@/AdminPanel/pages/Users/types/role-action.types';
import type {
  TUserDto,
  TUserResponse
} from '@/AdminPanel/pages/Users/types/user.types';

type TUserSliceProps = {
  users: TUserResponse[];
  positions: TPosition[];
  roles_actions: TRoleAction[];
  total: number | undefined;
  isLoading: boolean;
  user: TUserResponse | null;
  position: TPosition | null;
  role_action: TRoleAction | null;
};

const initialState: TUserSliceProps = {
  users: [],
  positions: [],
  roles_actions: [],
  total: 0,
  isLoading: false,
  user: null,
  position: null,
  role_action: null
};

type GetAdminUsersParams = {
  limit?: number;
  offset?: number;
  searchTerm?: string;
  role?: string;
  isActive?: string;
};

export const getAllPositions = createAsyncThunk(
  'admin/positions/getAllPositions',
  async (data: { limit?: number; page?: number; searchTerm?: string }) => {
    const res = await positionsApi.getAdminAllPositions(
      data.limit,
      data.page,
      data.searchTerm
    );
    return {
      total: res.total,
      positions: res.data
    };
  }
);

export const createPosition = createAsyncThunk(
  'admin/user/createPosition',
  async (data: TPosition) => {
    await positionsApi.createPosition(data);
  }
);
export const updatePosition = createAsyncThunk(
  'admin/user/updatePosition',
  async (data: TPosition) => {
    await positionsApi.updatePosition(data);
  }
);

export const getPositionById = createAsyncThunk(
  'admin/getPositionById',
  async (userId: string) => {
    return positionsApi.getPositionById(userId);
  }
);

export const getRolesActions = createAsyncThunk(
  'admin/role_actions/getRolesActions',
  async (data: { limit?: number; page?: number; searchTerm?: string }) => {
    const res = await rolesActionsApi.getAllRolesActions(
      data.limit,
      data.page,
      data.searchTerm
    );
    return {
      total: res.total,
      roles_actions: res.data
    };
  }
);

export const createRolesAction = createAsyncThunk(
  '/admin/users/creteRolesActions',
  async (data: TRoleAction) => {
    await rolesActionsApi.createRolesAction(data);
  }
);

export const updateRolesAction = createAsyncThunk(
  'admin/users/updateRolesAction',
  async (data: TRoleAction) => {
    await rolesActionsApi.updateRolesAction(data);
  }
);

export const getRolesById = createAsyncThunk(
  'admin/getRolesById',
  async (userId: string) => {
    return rolesActionsApi.getRolesById(userId);
  }
);

export const getUsersById = createAsyncThunk(
  'admin/getUserById',
  async (userId: string) => {
    return usersApi.getAdminUserById(userId);
  }
);

export const getAdminAllUsers = createAsyncThunk(
  'admin/getAdminAllUsers',
  async (data: GetAdminUsersParams) => {
    const res = await usersApi.getAdminAllUsers(
      data.limit,
      data.offset,
      data.searchTerm,
      data.role,
      data.isActive !== undefined ? String(data.isActive) : undefined
    );
    return {
      total: res.total,
      users: res.data
    };
  }
);

export const createUser = createAsyncThunk(
  'admin/users/create',
  async (data: TUserDto) => {
    return usersApi.createAdminUser(data);
  }
);

export const updateUser = createAsyncThunk(
  'admin/users/update',
  async (data: { uuid: string; body: Partial<TUserDto> }) => {
    return await usersApi.updateAdminUser(data.uuid, data.body);
  }
);

const adminUsersSlice = createSlice({
  name: 'admin/users',
  initialState,
  reducers: {
    resetUserState(state) {
      state.user = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminAllUsers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAdminAllUsers.fulfilled, (state, action) => {
        state.users = action.payload.users;
        state.total = action.payload.total;
        state.isLoading = false;
      })
      .addCase(getRolesActions.fulfilled, (state, action) => {
        state.roles_actions = action.payload.roles_actions;
        state.isLoading = false;
        state.total = action.payload.total;
      })
      .addCase(getAllPositions.fulfilled, (state, action) => {
        state.positions = action.payload.positions;
        state.isLoading = false;
        state.total = action.payload.total;
      })
      .addCase(getUsersById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
      })
      .addCase(getUsersById.pending, (state) => {
        state.isLoading = true;
        state.user = null;
      })
      .addCase(getUsersById.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getPositionById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.position = action.payload;
      })
      .addCase(getPositionById.pending, (state) => {
        state.isLoading = true;
        state.position = null;
      })
      .addCase(getPositionById.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getRolesById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.role_action = action.payload;
      })
      .addCase(getRolesById.pending, (state) => {
        state.isLoading = true;
        state.role_action = null;
      })
      .addCase(getRolesById.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

export const { resetUserState } = adminUsersSlice.actions;
export default adminUsersSlice;
